import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import founder from '../assets/founder.png';
import founderHover from '../assets/founder-hover.jpeg';
import Header from "./Header";
import Footer from "./Footer";

const Founder = () => {
    const { hash } = useLocation();
    const [images, setImages] = useState([]);
    const [members, setMembers] = useState([]);
    const [teamLoading, setTeamLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [isScrolled, setIsScrolled] = useState(false);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        const fetchImages = async (url) => {
            try {
                if (url.startsWith("http:")) {
                    url = url.replace("http:", "https:");
                }
                const response = await axios.get(url);
                setImages(prev => [...prev, ...response.data.results]);
                if (response.data.next) {
                    fetchImages(response.data.next.replace("http:", "https:"));
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error loading images:", error);
                setLoading(false);
            }
        };
        fetchImages('https://kyrgyz-adventure-travel.com/api/v1/tours/about-us-images-list');
    }, []);

    useEffect(() => {
        axios.get('https://kyrgyz-adventure-travel.com/api/v1/tours/team/')
            .then(response => {
                setMembers(response.data.results || []);
                setTeamLoading(false);
            })
            .catch(error => {
                console.error("Error loading team: ", error);
                setTeamLoading(false);
            });
    }, []);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (!hash) return undefined;

        const timeout = setTimeout(() => {
            const target = document.querySelector(hash);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300);

        return () => clearTimeout(timeout);
    }, [hash, members.length]);

    useEffect(() => {
        if (lightbox === null) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setLightbox(null);
            if (e.key === 'ArrowRight') setLightbox(i => (i + 1) % images.length);
            if (e.key === 'ArrowLeft') setLightbox(i => (i - 1 + images.length) % images.length);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightbox, images.length]);

    useEffect(() => {
        document.body.style.overflow = lightbox !== null ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [lightbox]);

    const imageElements = useMemo(() => {
        return images.map((image, index) => (
            <div
                key={index}
                onClick={() => setLightbox(index)}
                className="rounded-3xl bg-cover bg-center aspect-square w-full md:w-[calc(33.333%-16px)] cursor-pointer hover:scale-105 active:scale-95 transition-transform duration-300 group relative overflow-hidden hover:z-10"
                style={{ backgroundImage: `url(${image.image})` }}
            >
                <div className="absolute inset-0 bg-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center rounded-3xl">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M1 1h6M1 1v6M17 17h-6M17 17v-6M1 17h6M1 17v-6M17 1h-6M17 1v6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </div>
                </div>
            </div>
        ));
    }, [images]);

    return (
        <div className="bg-dark min-h-screen">
            <Header isScrolled={isScrolled} />
            {/* Spacer to clear fixed header */}
            <div className="h-32" />

            <div className="container pb-20">
                {/* About section */}
                <div className="flex flex-col md:flex-row gap-16 items-center mb-24">
                    <div className="w-full md:w-1/2 flex flex-col gap-6">
                        <p className="font-unbounded text-it-green text-xs uppercase tracking-widest">
                            About us
                        </p>
                        <h1 className="font-unbounded font-bold text-4xl md:text-5xl text-white leading-tight">
                            The founder<br />
                            <span className="text-gold">Angela</span>
                        </h1>
                        <div className="flex flex-col gap-4 font-manrope text-white/70 text-base md:text-lg leading-relaxed">
                            <p>Hello, my name is Angela, and I am the founder of Kyrgyz Adventure Travel, based in Bishkek - in the heart of Central Asia, in the beautiful country of Kyrgyzstan.</p>
                            <p>I was born in Bishkek, but later moved to Italy, where I spent 5 wonderful years living in the heart of Tuscany. This experience allowed me to deeply understand European culture, travel expectations, and the level of service international travelers are looking for.</p>
                            <p>After returning to my homeland, I was inspired to develop tourism in Kyrgyzstan and share its authentic beauty with the world.</p>
                            <p>With over 10 years of experience in the travel industry, I am always personally available to design unique and tailor-made journeys, carefully adapted to the preferences and needs of each traveler.</p>
                            <p>My goal is not just to organize trips, but to create meaningful experiences and unforgettable memories in one of the most untouched and fascinating destinations in the world.</p>
                        </div>
                    </div>
                    <div className="w-full md:w-1/2 flex justify-center">
                        <div className="relative rounded-3xl w-2/3 sm:w-1/2 md:w-full max-w-sm overflow-hidden group">
                            <img
                                src={founder}
                                className="w-full object-cover transition-opacity duration-500 group-hover:opacity-0"
                                alt="Founder Angela"
                                loading="lazy"
                            />
                            <img
                                src={founderHover}
                                className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                                alt="Founder Angela"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>

                <section id="team" className="mb-24 scroll-mt-32">
                    <div className="flex flex-col items-center gap-4 mb-16">
                        <p className="font-unbounded text-gold text-xs uppercase tracking-widest">
                            Meet the people
                        </p>
                        <h2 className="font-unbounded font-bold text-4xl md:text-5xl text-white text-center">
                            Our Team
                        </h2>
                        <p className="font-manrope text-white/60 text-lg text-center max-w-xl mt-2">
                            Passionate locals dedicated to making your adventure unforgettable
                        </p>
                    </div>

                    {members.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {members.map((member) => (
                                <div key={member.id} className="card-dark overflow-hidden group">
                                    <div className="relative overflow-hidden">
                                        <img
                                            src={member.photo}
                                            className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105"
                                            alt={member.name}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-dark-card via-transparent to-transparent" />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="font-unbounded font-bold text-white text-lg">
                                            {member.name}
                                        </h3>
                                        <p className="font-unbounded text-gold text-xs uppercase tracking-widest mt-1 mb-3">
                                            {member.position}
                                        </p>
                                        {member.description && (
                                            <p className="font-manrope text-white/60 text-sm leading-relaxed">
                                                {member.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !teamLoading ? (
                        <p className="font-manrope text-white/50 text-center max-w-xl mx-auto">
                            Our team details are being updated.
                        </p>
                    ) : null}
                </section>

                {/* Gallery */}
                <div className="flex flex-col gap-4 mb-12">
                    <p className="font-unbounded text-it-green text-xs uppercase tracking-widest text-center">Gallery</p>
                    <h2 className="font-unbounded font-bold text-3xl text-white text-center">Our moments</h2>
                </div>

                <div className="flex flex-wrap gap-5">
                    {loading ? (
                        Array(6).fill(null).map((_, i) => (
                            <div key={i} className="w-full md:w-[calc(33.333%-14px)] rounded-3xl bg-dark-card animate-pulse aspect-square" />
                        ))
                    ) : imageElements}
                </div>
            </div>

            <Footer />

            {/* Lightbox */}
            {lightbox !== null && (
                <div
                    className="lightbox-backdrop fixed inset-0 z-[100] flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.92)' }}
                    onClick={() => setLightbox(null)}
                >
                    <button
                        onClick={() => setLightbox(null)}
                        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors duration-200 z-10"
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M1 1l16 16M17 1L1 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>

                    <div className="absolute top-6 left-1/2 -translate-x-1/2 font-unbounded text-white/50 text-xs tracking-widest">
                        {lightbox + 1} / {images.length}
                    </div>

                    <button
                        onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); }}
                        className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-all duration-200 hover:scale-110"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M13 4l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>

                    <div onClick={(e) => e.stopPropagation()} className="relative max-w-5xl w-full mx-16 md:mx-24">
                        <img
                            key={lightbox}
                            src={images[lightbox]?.image}
                            alt=""
                            className="w-full max-h-[80vh] object-contain rounded-2xl lightbox-img"
                        />
                    </div>

                    <button
                        onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); }}
                        className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-all duration-200 hover:scale-110"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default Founder;
